import { displayMode } from "@/lib/pwa";

/**
 * Room for the window controls when the installed app runs in a WINDOW on
 * iPadOS 26. Ported from PropPay LX (lib/window-chrome.ts).
 *
 * iPadOS 26 draws a window's close / minimise / expand controls over the
 * top-left of the app's own content. UIKit gives a native app a title-bar
 * safe area for them; a Home Screen web app gets nothing: `env(safe-area-
 * inset-top)` stays 0, and WebKit does not implement the Window Controls
 * Overlay API (`titlebar-area-*`), so there is no way to ask where they are.
 * Left alone they sit on top of the menu button, the logo, or whatever else
 * is first in the header.
 *
 * So the page reserves the band itself, but only when it can tell it is in a
 * window: an installed app, on an iPad, whose window does not fill the
 * screen in either orientation. Full screen — including a window expanded
 * with the green control, where iPadOS hides the controls again — matches
 * one of the screen's dimensions and gets nothing. The value is published as
 * `--window-chrome-top` and folded into `--sa-top` (index.css), so the app
 * header, the sign-in bar, the service banner, the docked sidebar, the route
 * progress line and the toast stack (through the header's measured
 * `--app-header-height`) all move down together without any of them knowing
 * why. Re-read on resize, because a window can be dragged between sizes and
 * into full screen without a reload.
 *
 * The 28px is the height of the control band, the same as a macOS title
 * bar. It is a constant because the device offers no number to read; keep it
 * scoped to this one detected mode and do not let it leak into the general
 * inset logic (the standing rule against hardcoded status-bar clearance).
 */
export const WINDOW_CHROME_TOP_PX = 28;

export interface WindowMetrics {
  standalone: boolean;
  iPad: boolean;
  innerWidth: number;
  innerHeight: number;
  screenWidth: number;
  screenHeight: number;
}

/** How much iPadOS reserves for itself above a full-screen app, at most. */
const FULL_SCREEN_SLACK = 100;

/** The band to reserve, in CSS px — 0 unless this is a windowed iPad app. */
export function windowChromeTop(m: WindowMetrics): number {
  if (!m.standalone || !m.iPad) return 0;
  const long = Math.max(m.screenWidth, m.screenHeight);
  const short = Math.min(m.screenWidth, m.screenHeight);
  // `screen.*` on iPadOS reports the panel in one fixed orientation, so a
  // full-screen app spans either (long × short) or (short × long). A window
  // spans neither: at least one of its sides is smaller than it would be.
  const fillsLandscape = Math.abs(m.innerWidth - long) <= 1 && m.innerHeight >= short - FULL_SCREEN_SLACK;
  const fillsPortrait = Math.abs(m.innerWidth - short) <= 1 && m.innerHeight >= long - FULL_SCREEN_SLACK;
  return fillsLandscape || fillsPortrait ? 0 : WINDOW_CHROME_TOP_PX;
}

export function isIPad(nav: Pick<Navigator, "userAgent" | "platform" | "maxTouchPoints"> = navigator): boolean {
  // iPadOS asks for desktop sites and calls itself a Mac; the touch points
  // give it away. The UA check covers "Request Mobile Website".
  return /iPad/.test(nav.userAgent) || (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
}

function metrics(): WindowMetrics {
  return {
    standalone: displayMode() === "standalone",
    iPad: isIPad(),
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  };
}

/** Publish `--window-chrome-top` on <html>, now and on every resize. */
export function initWindowChrome(): void {
  if (typeof window === "undefined") return;
  const root = document.documentElement;
  const publish = () => {
    const px = windowChromeTop(metrics());
    if (px) root.style.setProperty("--window-chrome-top", `${px}px`);
    else root.style.removeProperty("--window-chrome-top");
  };
  publish();
  window.addEventListener("resize", publish);
  window.addEventListener("orientationchange", publish);
}
